import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";
import { getApiErrorMessage } from "../../utils/apiError";

import { receivableTemplateApi } from "../../api/receivableTemplateApi";

import type {
  ReceivableTemplate,
  ReceivableTemplateSearchPayload,
} from "../../types/receivableTemplateTypes";

import {
  uploadMediaFiles,
  createResolvedMedia,
  buildRenamePayload,
  applyRename,
  buildDeletePayload,
  applyDelete,
} from "../../utils/mediaUtils";
import { mediaLinkKey, resolveMediaLinks } from "../../utils/mediaLinkBatch";

export const EMAIL_TEMPLATE_TYPE = 1;
export const ALL_CATEGORY = 1;
/** "Use for" values a template can carry: All, Reminder emails, Formal notices, Legal notices. */
export const TEMPLATE_CATEGORIES = [1, 2, 3, 4];

interface EmailTemplatesState {
  items: ReceivableTemplate[];
  selectedTemplate: ReceivableTemplate | null;

  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;

  hasMore: boolean;
  pageIndex: number;
  totalCount: number;

  error: string | null;
}

const initialState: EmailTemplatesState = {
  items: [],
  selectedTemplate: null,

  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,

  hasMore: true,
  pageIndex: 0,
  totalCount: 0,

  error: null,
};

const emailTemplatesSlice = createSlice({
  name: "emailTemplates",

  initialState,

  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },

    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },

    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },

    setEmailTemplates: (state, action: PayloadAction<ReceivableTemplate[]>) => {
      state.items = action.payload;
    },

    setSelectedTemplate: (
      state,
      action: PayloadAction<ReceivableTemplate | null>,
    ) => {
      state.selectedTemplate = action.payload;
    },

    appendEmailTemplates: (
      state,
      action: PayloadAction<ReceivableTemplate[]>,
    ) => {
      state.items = [...state.items, ...action.payload];
    },

    addSingleEmailTemplateLocally: (
      state,
      action: PayloadAction<ReceivableTemplate>,
    ) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },

    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },

    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },

    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },

    resetEmailTemplateList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
      state.selectedTemplate = null;
      state.error = null;
    },

    updateSingleEmailTemplate: (
      state,
      action: PayloadAction<
        Partial<ReceivableTemplate> & {
          id: string;
        }
      >,
    ) => {
      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );

      if (index !== -1) {
        state.items[index] = {
          ...state.items[index],
          ...action.payload,
        };
      }

      if (state.selectedTemplate?.id === action.payload.id) {
        state.selectedTemplate = {
          ...state.selectedTemplate,
          ...action.payload,
        };
      }
    },

    removeSingleEmailTemplate: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);

      if (state.selectedTemplate?.id === action.payload) {
        state.selectedTemplate = null;
      }

      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,

  setEmailTemplates,
  setSelectedTemplate,
  appendEmailTemplates,

  addSingleEmailTemplateLocally,

  setTotalCount,
  setHasMore,
  setPageIndex,

  resetEmailTemplateList,

  updateSingleEmailTemplate,
  removeSingleEmailTemplate,
} = emailTemplatesSlice.actions;

// The newest first-page request. A fresh search term must not be dropped
// because an earlier one is still loading, so a first page always runs and an
// answer to anything older than it is discarded.
let latestFirstPage = 0;

export const fetchEmailTemplates = (
  payload: ReceivableTemplateSearchPayload = {},
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().emailTemplates;

    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    const isFirstPage = incomingPageIndex === 0;
    if ((state.isLoading || !state.hasMore) && !isFirstPage) {
      return;
    }
    const ticket = isFirstPage ? ++latestFirstPage : latestFirstPage;
    const superseded = () => ticket !== latestFirstPage;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        companyId: userData.companyId,

        isAppAdmin: userData.isAppAdmin ?? true,

        pageSize: payload.pageSize || 10,

        pageIndex: incomingPageIndex,

        orderBy: payload.orderBy || "modificationdate",

        sortAscending: payload.sortAscending ?? false,

        includeFacets: true,

        groupIds: [`${userData.companyId}`],

        includeDocument: payload.includeDocument !== false,

        ids: payload.ids || [],

        // Admins' "Show Deleted" filter.

        includeDeleted: payload.includeDeleted ?? false,

        categories: payload.categories?.length ? payload.categories : TEMPLATE_CATEGORIES,

        templateType: EMAIL_TEMPLATE_TYPE,

        isActive: payload.isActive ?? null,

        createdBy: (payload.createdBy || []).map((item: any) =>
          typeof item === "object" ? item.id : item,
        ),

        updatedBy: (payload.updatedBy || []).map((item: any) =>
          typeof item === "object" ? item.id : item,
        ),

        currentProfileId: userData.currentLoggedInUserId,

        myMode: payload.myMode ?? true,

        searchText:
          typeof payload.searchText === "object"
            ? payload.searchText?.label || payload.searchText?.id || ""
            : String(payload.searchText || "").trim(),

        ...(payload.startDate
          ? {
              startDate: new Date(payload.startDate).toISOString(),
            }
          : {}),

        ...(payload.endDate
          ? {
              endDate: new Date(payload.endDate).toISOString(),
            }
          : {}),
      };

      const response =
        await receivableTemplateApi.searchReceivableTemplate(requestBody);

      if (superseded()) return;

      const fetchedData: ReceivableTemplate[] = response.data || [];

      const totalCount = response.count || 0;

      dispatch(setTotalCount(totalCount));

      // One media request for the page, not one per template.
      const ATTACHMENT_MEDIA_TYPE = 3;
      const links = await resolveMediaLinks(
        fetchedData,
        (item, want) => {
          for (const media of (item.medias || []) as any[]) {
            want(item.groupId, ATTACHMENT_MEDIA_TYPE, media.s3Url);
          }
        },
        receivableTemplateApi.getMediaLink,
        "Email Template",
      );

      if (superseded()) return;

      const resolvedData = fetchedData.map((templateItem: ReceivableTemplate) => {
        const updatedTemplate = {
          ...templateItem,
          id: templateItem.id,
        };

        if (templateItem.medias && templateItem.medias.length > 0) {
          updatedTemplate.medias = templateItem.medias.map((media: any) => ({
            ...media,
            url: links.get(mediaLinkKey(ATTACHMENT_MEDIA_TYPE, media.s3Url)) || "",
          }));
          updatedTemplate.mediaLinks = null;
        }

        return updatedTemplate;
      });

      const currentLoadedCount =
        incomingPageIndex === 0
          ? resolvedData.length
          : state.items.length + resolvedData.length;

      if (currentLoadedCount >= totalCount || resolvedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setEmailTemplates(resolvedData));

        dispatch(setPageIndex(1));
      } else {
        dispatch(appendEmailTemplates(resolvedData));

        dispatch(setPageIndex(incomingPageIndex + 1));
      }

      return resolvedData;
    } catch (error: any) {
      console.error("Fetch Email Templates Error:", error);

      dispatch(setHasMore(false));

      toast.error(
        i18n.t("common:slices.emailTemplates.fetchError", {
          defaultValue: "Failed to fetch email templates",
        }),
      );

      throw error;
    } finally {
      if (!superseded()) dispatch(setLoading(false));
    }
  };
};

export const getEmailTemplateById = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setLoading(true));

      const result = await dispatch(
        fetchEmailTemplates({
          ids: [id],
          pageIndex: 0,
          pageSize: 1,
        }),
      );

      const template = Array.isArray(result) ? result[0] : undefined;

      if (!template) {
        throw new Error(i18n.t("slices.emailTemplates.notFound", { ns: "common" }));
      }

      dispatch(setSelectedTemplate(template));

      return template;
    } catch (error) {
      console.error("Get Email Template Error:", error);

      toast.error(
        i18n.t("common:slices.emailTemplates.fetchDetailError", {
          defaultValue: "Failed to fetch email template details",
        }),
      );

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addEmailTemplate = (data: Partial<ReceivableTemplate>) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setAdding(true));

      const apiPayload = {
        ...data,

        templateType: EMAIL_TEMPLATE_TYPE,

        category: data.category ?? ALL_CATEGORY,
      };

      const res =
        await receivableTemplateApi.createReceivableTemplate(apiPayload);

      const newTemplate: ReceivableTemplate = {
        ...data,

        id: res?.id || res,

        templateType: EMAIL_TEMPLATE_TYPE,

        category: data.category ?? ALL_CATEGORY,

        isActive: true,

        medias: data.medias || [],

        receivableTemplatePlaceholders:
          data.receivableTemplatePlaceholders || [],

        mediaLinks: data.mediaLinks || null,

        placeholders: data.placeholders || null,

        creationDate: new Date().toISOString(),

        modificationDate: new Date().toISOString(),

        createdBy: data.createdBy || "",

        modifiedBy: data.modifiedBy || "",

        createdByName: data.createdByName || "",

        modifiedByName: data.modifiedByName || "",

        receivableTemplateName: data.receivableTemplateName || "",

        htmlContent: data.htmlContent || "",

        receivableTemplateJson: data.receivableTemplateJson || "",
      };

      const { items } = getState().emailTemplates;

      dispatch(setEmailTemplates([newTemplate, ...items]));

      toast.success(
        i18n.t("emailTemplate:emailTemplate.messages.addSuccess", {
          defaultValue: "Email Template created successfully",
        }),
      );

      return res;
    } catch (error) {
      console.error("Create Email Template Error:", error);

      toast.error(
        i18n.t("emailTemplate:emailTemplate.messages.addError", {
          defaultValue: "Failed to create email template",
        }),
      );

      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editEmailTemplate = (
  data: Partial<ReceivableTemplate> & {
    id: string;
  },
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setEditing(true));

      const apiPayload = {
        ...data,

        id: data.id,

        templateType: EMAIL_TEMPLATE_TYPE,

        category: data.category ?? ALL_CATEGORY,
      };

      await receivableTemplateApi.updateReceivableTemplate(apiPayload);

      const { items } = getState().emailTemplates;

      const updatedList = items.map((item: ReceivableTemplate) =>
        item.id === data.id
          ? {
              ...item,
              ...data,
              templateType: EMAIL_TEMPLATE_TYPE,
              category: data.category ?? ALL_CATEGORY,
            }
          : item,
      );

      dispatch(setEmailTemplates(updatedList));

      toast.success(
        i18n.t("emailTemplate:emailTemplate.messages.editSuccess", {
          defaultValue: "Email Template updated successfully",
        }),
      );

      return data;
    } catch (error) {
      console.error("Edit Email Template Error:", error);

      toast.error(
        i18n.t("emailTemplate:emailTemplate.messages.editError", {
          defaultValue: "Failed to update email template",
        }),
      );

      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const uploadEmailTemplateImages = (
  templateId: string,
  files: File[],
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!files?.length) return;

      const userId = (await getCurrentUser()).userId;

      const { payloads, links } = await uploadMediaFiles(
        receivableTemplateApi,
        templateId,
        files,
      );

      const newEntries = createResolvedMedia(
        payloads,
        links,
        templateId,
        userId,
      );

      const state = getState();

      const updatedItems = state.emailTemplates.items.map(
        (item: ReceivableTemplate) =>
          item.id === templateId
            ? {
                ...item,

                medias: [...(item.medias || []), ...newEntries],

                mediaLinks: null,
              }
            : item,
      );

      dispatch(setEmailTemplates(updatedItems));

      toast.success(
        i18n.t("common:slices.emailTemplates.uploadSuccess", {
          count: files.length,
          defaultValue: `${files.length} files uploaded successfully`,
        }),
      );
    } catch (error) {
      console.error("Email Template Upload Error:", error);

      toast.error(
        i18n.t("common:slices.emailTemplates.uploadError", {
          defaultValue: "Failed to upload files",
        }),
      );

      throw error;
    }
  };
};

export const renameEmailTemplateImage = (
  templateId: string,
  fileId: string,
  newName: string,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const state = getState();

      const currentTemplate = state.emailTemplates.items.find(
        (item: ReceivableTemplate) => item.id === templateId,
      );

      const targetFile = currentTemplate?.medias?.find(
        (media: any) => media.id === fileId,
      );

      if (!targetFile) {
        toast.error(i18n.t("slices.shared.fileNotFound", { ns: "common" }));
        return;
      }

      const payload = buildRenamePayload(targetFile, templateId, newName);

      await receivableTemplateApi.uploadMedia(templateId, payload);

      const updatedItems = state.emailTemplates.items.map(
        (item: ReceivableTemplate) =>
          item.id === templateId
            ? {
                ...item,
                medias: applyRename(item.medias || [], fileId, newName),
              }
            : item,
      );

      dispatch(setEmailTemplates(updatedItems));
    } catch (error) {
      console.error("Email Template Media Rename Error:", error);

      toast.error(
        i18n.t("common:slices.emailTemplates.renameError", {
          defaultValue: "Failed to rename file",
        }),
      );

      throw error;
    }
  };
};

export const removeEmailTemplateImage = (
  templateId: string,
  filesToRemove: Array<{
    id: string;
  }>,
) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      if (!filesToRemove?.length) {
        return;
      }

      const state = getState();

      const currentTemplate = state.emailTemplates.items.find(
        (item: ReceivableTemplate) => item.id === templateId,
      );

      if (!currentTemplate?.medias) {
        return;
      }

      const idsToRemove = filesToRemove.map((file) => file.id);

      const payload = buildDeletePayload(
        currentTemplate.medias,
        templateId,
        idsToRemove,
      );
      // Nothing to remove that this record still lists.
      if (payload.length === 0) return;

      await receivableTemplateApi.uploadMedia(templateId, payload);

      const updatedItems = state.emailTemplates.items.map(
        (item: ReceivableTemplate) =>
          item.id === templateId
            ? {
                ...item,
                medias: applyDelete(item.medias || [], idsToRemove),
              }
            : item,
      );

      dispatch(setEmailTemplates(updatedItems));
    } catch (error) {
      console.error("Email Template Remove Media Error:", error);

      toast.error(
        i18n.t("common:slices.emailTemplates.deleteImageError", {
          defaultValue: "Failed to remove file",
        }),
      );

      throw error;
    }
  };
};

export const handleActivateEmailTemplate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setLoading(true));

      await receivableTemplateApi.activateTemplate(id);

      const { items } = getState().emailTemplates;

      dispatch(
        setEmailTemplates(
          items.map((item: ReceivableTemplate) =>
            item.id === id
              ? {
                  ...item,
                  isActive: true,
                }
              : item,
          ),
        ),
      );

      toast.success(
        i18n.t("common:slices.emailTemplates.activateSuccess", {
          defaultValue: "Email Template activated successfully",
        }),
      );
    } catch (error) {
      console.error("Activate Email Template Error:", error);

      toast.error(
        i18n.t("common:slices.emailTemplates.activateError", {
          defaultValue: "Failed to activate email template",
        }),
      );

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const handleDeactivateEmailTemplate = (id: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setLoading(true));

      await receivableTemplateApi.deActivateTemplate(id);

      const { items } = getState().emailTemplates;

      dispatch(
        setEmailTemplates(
          items.map((item: ReceivableTemplate) =>
            item.id === id
              ? {
                  ...item,
                  isActive: false,
                }
              : item,
          ),
        ),
      );

      toast.success(
        i18n.t("common:slices.emailTemplates.deactivateSuccess", {
          defaultValue: "Email Template deactivated successfully",
        }),
      );
    } catch (error) {
      console.error("Deactivate Email Template Error:", error);

      toast.error(
        i18n.t("common:slices.emailTemplates.deactivateError", {
          defaultValue: "Failed to deactivate email template",
        }),
      );

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const removeEmailTemplate = (id: string, reason: string) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setDeleting(true));

      await receivableTemplateApi.removeReceivableTemplate(id, reason);

      const { items } = getState().emailTemplates;

      dispatch(
        setEmailTemplates(
          items.filter((item: ReceivableTemplate) => item.id !== id),
        ),
      );

      toast.success(
        i18n.t("emailTemplate:emailTemplate.messages.removeSuccess", {
          defaultValue: "Email Template removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Email Template Error:", error);

      toast.error(
        getApiErrorMessage(
          error,
          i18n.t("emailTemplate:emailTemplate.messages.removeError", {
            defaultValue: "Failed to remove email template",
          }),
        ),
      );

      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default emailTemplatesSlice.reducer;
